import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EntityType, Entity, Attribute, Sample, Test, Assay, Order } from '../types';
import type { EventLogEntry, Attachment, ParserEventLogEntry } from '@mariana/labos-client';
import { limsClient } from '../api/limsClient';
import { capitalize } from '../utils/formatValue';
import { fetchEntitiesByIds, resolveSourceColumns } from '../utils/resolveColumns';
import type { ResolvedEntityMap } from '../utils/resolveColumns';
import { ENTITY_COLUMNS } from '../config/viewsConfig';
import { useLab, entityHasLabId } from '../LabContext';
import type { RelatedEntities, ReferencedEntity } from './entityDetailTypes';
import { ResultsViewer } from './ResultsViewer';
import { EntityDetailAttributesTab } from './EntityDetailAttributesTab';
import { EntityDetailRelationshipsTab } from './EntityDetailRelationshipsTab';
import { EntityDetailEventsTab } from './EntityDetailEventsTab';
import { EntityDetailAttachmentsTab } from './EntityDetailAttachmentsTab';
import { EntityHistoryTimeline } from './EntityHistory';
import './EntityDetail.css';

interface EntityDetailProps {
  entityType: EntityType;
  entityId: number;
}

type TabId = 'attributes' | 'relationships' | 'events' | 'attachments' | 'worksheets' | 'history';

const VALID_TABS: TabId[] = ['attributes', 'relationships', 'events', 'attachments', 'worksheets', 'history'];

export const EntityDetail: React.FC<EntityDetailProps> = ({
  entityType,
  entityId,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { labSelection, setLabSelection } = useLab();
  const [entity, setEntity] = useState<Entity | null>(null);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [referencedEntities, setReferencedEntities] = useState<ReferencedEntity[]>([]);
  const [relatedEntities, setRelatedEntities] = useState<RelatedEntities>({});
  const [referencingFullEntities, setReferencingFullEntities] = useState<Record<string, Entity[]>>({});
  const [referencingResolvedEntities, setReferencingResolvedEntities] = useState<Record<string, ResolvedEntityMap>>({});
  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [parserEvents, setParserEvents] = useState<ParserEventLogEntry[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const tabParam = searchParams.get('tab');
    return tabParam && VALID_TABS.includes(tabParam as TabId) ? (tabParam as TabId) : 'attributes';
  });

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    setActiveTab(tabParam && VALID_TABS.includes(tabParam as TabId) ? (tabParam as TabId) : 'attributes');
  }, [entityType, entityId]);

  const setActiveTabAndURL = (tab: TabId) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    if (tab === 'attributes') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    const loadEntity = async () => {
      try {
        setLoading(true);
        setError(null);

        // Step 1: Fetch the entity (must come first)
        const entityData = await limsClient.getEntityByType(entityType, entityId);
        setEntity(entityData);

        // Auto-sync lab selector to match the entity's lab
        if (entityHasLabId(entityType) && entityData) {
          const entityLabId = entityData.attributes?.lab_id ?? 1;
          if (entityLabId !== labSelection) {
            setLabSelection(entityLabId);
          }
        }

        // Steps 3–9 in parallel (independent of each other)
        const [
          attrsResult,
          referencedResult,
          referencingResult,
          eventsResult,
          parserEventsResult,
          attachmentsResult,
        ] = await Promise.allSettled([
          limsClient.getAttributes(entityType),
          limsClient.getReferencedEntities(entityType, entityId),
          limsClient.getEntitiesReferencing(entityType, entityId),
          limsClient.getEvents({ entityType, entityId }),
          entityType === 'test'
            ? limsClient.getParserEvents({ testId: entityId })
            : Promise.resolve(null),
          limsClient.getAttachments({
            filters: [
              { attribute_name: 'parent_entity_type', operator: '=', value: entityType },
              { attribute_name: 'parent_entity_id', operator: '=', value: entityId },
            ],
            limit: 50,
          }),
        ]);

        const entityAttrs = attrsResult.status === 'fulfilled' ? attrsResult.value : [];
        setAttributes(entityAttrs);

        // Process referenced entities
        if (referencedResult.status === 'fulfilled') {
          setReferencedEntities(referencedResult.value.map(ref => ({
            attributeName: ref.attributeName,
            entity: ref.entity,
            entityType: ref.entityType,
          })));
        } else {
          console.warn('Failed to load referenced entities:', referencedResult.reason);
          setReferencedEntities([]);
        }

        const related: RelatedEntities = {};
        if (referencingResult.status === 'fulfilled') {
          const { entities: referencingMap, attributeNames } = referencingResult.value;
          for (const [otherEntityType, entityIds] of Object.entries(referencingMap)) {
            const entityIdsArray = entityIds as number[];
            if (entityIdsArray.length > 0) {
              const firstEntityKey = `${otherEntityType}:${entityIdsArray[0]}`;
              let attributeName = attributeNames[firstEntityKey];

              if (!attributeName || attributeName === 'unknown') {
                try {
                  const otherEntityAttrs = await limsClient.getAttributes(otherEntityType as EntityType);
                  const referencingAttr = otherEntityAttrs.find(
                    attr => attr.referenced_entity_type === entityType
                  );
                  attributeName = referencingAttr?.name || `${entityType}_id`;
                } catch {
                  attributeName = `${entityType}_id`;
                }
              }

              const maxDisplay = 10;
              const displayIds = entityIdsArray.slice(0, maxDisplay);

              related[otherEntityType as EntityType] = {
                ids: displayIds,
                totalCount: entityIdsArray.length,
                attributeName,
              };
            }
          }
        } else {
          console.warn('Failed to query entities referencing current entity:', referencingResult.reason);
        }
        setRelatedEntities(related);

        // Step 6: Fetch full entities for related (depends on step 5)
        const fullEntities: Record<string, Entity[]> = {};
        await Promise.all(
          Object.entries(related).map(async ([relType, relData]) => {
            if (!relData) return;
            try {
              fullEntities[relType] = await fetchEntitiesByIds(relType as EntityType, relData.ids);
            } catch {
              fullEntities[relType] = [];
            }
          })
        );
        setReferencingFullEntities(fullEntities);

        // Step 7: Resolve cross-entity columns (e.g. assay_id.title for tests)
        const resolvedMap: Record<string, ResolvedEntityMap> = {};
        await Promise.all(
          Object.entries(fullEntities).map(async ([relType, entities]) => {
            if (entities.length === 0) return;
            const columns = ENTITY_COLUMNS[relType as EntityType] || [];
            const hasCrossEntityCols = columns.some(col => col.key.includes('.'));
            if (!hasCrossEntityCols) return;
            try {
              resolvedMap[relType] = await resolveSourceColumns(entities, columns, relType as EntityType);
            } catch {
              resolvedMap[relType] = new Map();
            }
          })
        );
        setReferencingResolvedEntities(resolvedMap);

        // Process events
        if (eventsResult.status === 'fulfilled') {
          setEvents(eventsResult.value.data);
        } else {
          console.warn('Failed to load events:', eventsResult.reason);
          setEvents([]);
        }

        // Process parser events
        if (parserEventsResult.status === 'fulfilled' && parserEventsResult.value) {
          setParserEvents(parserEventsResult.value.data);
        } else if (parserEventsResult.status === 'rejected') {
          console.warn('Failed to load parser events:', parserEventsResult.reason);
          setParserEvents([]);
        }

        // Process attachments
        if (attachmentsResult.status === 'fulfilled') {
          setAttachments(attachmentsResult.value.data);
        } else {
          console.warn('Failed to load attachments:', attachmentsResult.reason);
          setAttachments([]);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load entity';
        setError(errorMessage);
        console.error('Failed to load entity:', err);
      } finally {
        setLoading(false);
      }
    };

    loadEntity();
  }, [entityType, entityId]);

  const entityTypeDisplay = capitalize(entityType);

  const handleEntityClick = (otherEntityType: EntityType, otherEntityId: number) => {
    navigate(`/${otherEntityType}/${otherEntityId}`);
  };

  if (loading) {
    return (
      <div className="entity-detail-page">
        <div className="entity-detail-loading">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="entity-detail-page">
        <div className="entity-detail-header-row">
          <h2 className="entity-detail-title">{entityTypeDisplay} Details</h2>
        </div>
        <div className="entity-detail-error">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="entity-detail-page">
        <div className="entity-detail-header-row">
          <h2 className="entity-detail-title">{entityTypeDisplay} Details</h2>
        </div>
        <div className="entity-detail-empty">
          <p>Entity not found</p>
        </div>
      </div>
    );
  }

  const relationshipCount = referencedEntities.length +
    Object.values(relatedEntities).reduce((sum, r) => sum + (r?.totalCount ?? 0), 0);

  return (
    <div className="entity-detail-page">
      <div className="entity-detail-container">
        <div className="entity-detail-header-row">
          <h2 className="entity-detail-title">{entityTypeDisplay} #{entity.id}</h2>
          <div className="entity-detail-tabs">
            <button
              className={`entity-detail-tab ${activeTab === 'attributes' ? 'active' : ''}`}
              onClick={() => setActiveTabAndURL('attributes')}
            >
              Attributes
            </button>
            {entityType === 'test' && (entity.attributes?.worksheet_data || entity.attributes?.raw_worksheet_data) && (
              <button
                className={`entity-detail-tab ${activeTab === 'worksheets' ? 'active' : ''}`}
                onClick={() => setActiveTabAndURL('worksheets')}
              >
                Worksheets
              </button>
            )}
            <button
              className={`entity-detail-tab ${activeTab === 'relationships' ? 'active' : ''}`}
              onClick={() => setActiveTabAndURL('relationships')}
            >
              Relationships <span className="tab-count">({relationshipCount})</span>
            </button>
            <button
              className={`entity-detail-tab ${activeTab === 'events' ? 'active' : ''}`}
              onClick={() => setActiveTabAndURL('events')}
            >
              Events <span className="tab-count">({events.length})</span>
            </button>
            <button
              className={`entity-detail-tab ${activeTab === 'attachments' ? 'active' : ''}`}
              onClick={() => setActiveTabAndURL('attachments')}
            >
              Attachments <span className="tab-count">({attachments.length})</span>
            </button>
            <button
              className={`entity-detail-tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTabAndURL('history')}
            >
              History
            </button>
          </div>
        </div>

        <div className="entity-detail-tab-content">
          {activeTab === 'attributes' && (
            <EntityDetailAttributesTab
              entity={entity}
              entityType={entityType}
              attributes={attributes}
              onEntityClick={handleEntityClick}
              onAttributeUpdate={async (attrName, value) => {
                await limsClient.updateEntityByType(entityType, entityId, { [attrName]: value });
                const updated = await limsClient.getEntityByType(entityType, entityId);
                setEntity(updated);
              }}
            />
          )}
          {activeTab === 'relationships' && (
            <EntityDetailRelationshipsTab
              entityType={entityType}
              entityId={entityId}
              referencedEntities={referencedEntities}
              relatedEntities={relatedEntities}
              referencingFullEntities={referencingFullEntities}
              referencingResolvedEntities={referencingResolvedEntities}
              onEntityClick={handleEntityClick}
              onNavigate={(path) => navigate(path)}
            />
          )}
          {activeTab === 'events' && (
            <EntityDetailEventsTab entityType={entityType} events={events} parserEvents={parserEvents} />
          )}
          {activeTab === 'attachments' && (
            <EntityDetailAttachmentsTab entityType={entityType} attachments={attachments} onNavigate={(path) => navigate(path)} />
          )}
          {activeTab === 'worksheets' && entityType === 'test' && (
            <ResultsViewer test={entity as Test} />
          )}
          {activeTab === 'history' && (
            <EntityHistoryTimeline entityType={entityType} entityId={entityId} />
          )}
        </div>
      </div>
    </div>
  );
};
